import React, { useState, useContext, useEffect } from 'react'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Box from '@mui/material/Box'

import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'
import LoadingButton from '@mui/lab/LoadingButton'
import Typography from '@mui/material/Typography'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'

import JoinFullIcon from '@mui/icons-material/JoinFullOutlined'
import JoinInnerIcon from '@mui/icons-material/JoinInnerOutlined'
import JoinLeftIcon from '@mui/icons-material/JoinLeftOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined'
import LoopIcon from '@mui/icons-material/LoopOutlined'

import { constructGraphComparisonUrl, getComparedContexts } from '../../routes'
import MultipleSelect from '../MultiSelect'
import usePageInfo from '../../hooks/usePageInfo'
import {
    useContextsStore,
    selectOwnerContexts,
    selectIsPublicView,
    selectGraphOwnerUsername,
} from '../../stores/useContextsStore'
import { UserContext } from '../../UserContext'

import { sendToAnalytics } from '../../Analytics'
import useCompareMode from './useCompareMode'
import { Tooltip } from '@mui/material'

import useMediaQuery from '@mui/material/useMediaQuery'

export default function CompareViewDialog({
    open,
    onClose,
    selectedComparisonView,
    onComparisonViewChange,
}) {
    // This is a falsy function. let's remove this
    // and instead use zustand.getState().comparisonMode
    const comparisonMode = identifyComparisonType()

    const [value, setValue] = useState(comparisonMode)
    const {
        compareMode,
        originalGraphLabel,
        currentGraphLabel,
        compareModeTitle,
        compareModeDescription,
    } = useCompareMode()
    const { label: currentGraphName } = usePageInfo()

    const [tempMode, setTempMode] = useState(selectedComparisonView)

    const comparedContexts = getComparedContexts()

    const [selectedContexts, addToSelectedContexts] = useState(comparedContexts)

    // Viewer username (for analytics) vs. graph-owner username (for the
    // compare URL — must point at the OWNER of the graph being compared).
    const { username: viewerUsername } = useContext(UserContext)
    const contexts = useContextsStore(selectOwnerContexts)
    const isPublic = useContextsStore(selectIsPublicView)
    const ownerUsername = useContextsStore(selectGraphOwnerUsername)
    const targetUsername = ownerUsername || viewerUsername

    const isMobile = useMediaQuery('(max-width:600px)')

    useEffect(() => {
        setTempMode(selectedComparisonView)
    }, [selectedComparisonView])

    useEffect(() => {
        sendToAnalytics({
            userId: viewerUsername,
            event: 'compare_graphs',
            data: {
                action: 'open_comparison_dialog',
                type: value,
            },
        })
    }, [open])

    const contextNames = contexts
        .map(c => c.name)
        .filter(contextToCompare => contextToCompare !== currentGraphName)

    const handleChange = (_, newValue) => {
        setValue(newValue)

        sendToAnalytics({
            userId: viewerUsername,
            event: 'compare_graphs',
            data: {
                action: 'change_comparison_type',
                type: newValue,
            },
        })
    }

    const swapComparisonContext = () => {
        let link_to_open =
            'http://' +
            userFactory.getHostSite() +
            '/' +
            userFactory.getCurrentUser() +
            '/'
        let app_to_open = ''
        let context_to_open = ''
        if (userFactory.getViewType() != 'external') {
            app_to_open += '/' + userFactory.getCurrentApp()
        }
        app_to_open += '?'

        const urlParams = new URLSearchParams(window.location.search)

        urlParams.forEach((key, item) => {
            if (item == 'addcontext') {
                context_to_open = key
            } else {
                app_to_open += item + '=' + key + '&'
            }
        })
        app_to_open += 'addcontext=' + userFactory.getCurrentContext()

        let final_redirect = link_to_open + context_to_open + app_to_open

        window.location.replace(final_redirect)
    }

    const visualisedButtonDisabled =
        value === comparisonMode &&
        selectedContexts.every(context => comparedContexts.includes(context)) &&
        tempMode === selectedComparisonView

    const showChangeViewOptions = zustand.getState().comparisonMode !== ''
    const isMultipleContextMode = compareMode.niche || compareMode.seo

    return (
        <Dialog open={open} onClose={onClose} fullWidth={true}>
            <DialogTitle
                sx={{ padding: 4, color: theme => theme.palette.text.primary }}
            >
                {'Compare Context Graphs:'}
            </DialogTitle>
            <DialogContent sx={{ paddingRight: 4, paddingLeft: 4 }}>
                <DialogContentText>
                    Use the
                    <Typography
                        variant="subtitle2"
                        component="span"
                        style={{ fontWeight: 600 }}
                    >
                        {` ${currentGraphName} `}
                    </Typography>
                    context graph and view how it
                </DialogContentText>
                <Tabs
                    value={value}
                    onChange={handleChange}
                    variant="fullWidth"
                    centered
                    sx={{
                        marginTop: theme => theme.spacing(2),
                        marginBottom: theme => theme.spacing(2),
                    }}
                >
                    <Tab icon={<JoinInnerIcon />} label="intersects with" />
                    <Tab icon={<JoinLeftIcon />} label="differs from" />
                    <Tab icon={<JoinFullIcon />} label="combines with" />
                    <Tab icon={<JoinInnerIcon />} label="embeds into" />
                </Tabs>
                <DialogContentText>the following contexts:</DialogContentText>
                <MultipleSelect
                    selectItems={contextNames}
                    label={'Contexts'}
                    initialValue={comparedContexts}
                    onChange={name => addToSelectedContexts(name)}
                />
                {showChangeViewOptions && (
                    <Box
                        sx={{
                            marginTop: 1,
                            flexDirection: 'column',
                            display: 'flex',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography variant="subtitle1" gutterBottom>
                                Select Graph Comparison View:
                            </Typography>
                            {!isMultipleContextMode && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'right',
                                    }}
                                >
                                    {!isMobile && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                marginRight: 0,
                                                cursor: 'pointer',
                                            }}
                                            onClick={swapComparisonContext}
                                        >
                                            reverse comparison
                                        </Typography>
                                    )}
                                    <Tooltip title="Reverse the graph comparison order">
                                        <IconButton
                                            sx={{ marginLeft: 1 }}
                                            color="primary"
                                            variant="outlined"
                                            size="small"
                                            onClick={swapComparisonContext}
                                        >
                                            <LoopIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}
                        </Box>
                        <FormControlLabel
                            control={
                                <Radio
                                    checked={tempMode === 'Current'}
                                    onChange={() => {
                                        setTempMode('Current')
                                    }}
                                />
                            }
                            label={currentGraphLabel}
                        />
                        <FormControlLabel
                            control={
                                <Radio
                                    checked={tempMode === 'Original'}
                                    onChange={() => setTempMode('Original')}
                                />
                            }
                            label={originalGraphLabel}
                        />
                        <FormControlLabel
                            control={
                                <Radio
                                    checked={
                                        tempMode ===
                                        `Compare Mode: ${compareModeTitle}`
                                    }
                                    onChange={() =>
                                        setTempMode(
                                            `Compare Mode: ${compareModeTitle}`
                                        )
                                    }
                                />
                            }
                            label={`Compare View: ${compareModeDescription}`}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    paddingRight: 4,
                    paddingLeft: 4,
                    paddingBottom: 4,
                    paddingTop: 0,
                }}
            >
                <Button
                    onClick={() => {
                        onClose()
                        setTempMode(selectedComparisonView)
                        setValue(comparisonMode)
                    }}
                    variant="outlined"
                >
                    Cancel
                </Button>
                <LoadingButton
                    color="info"
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    disabled={
                        selectedContexts.length === 0 ||
                        visualisedButtonDisabled
                    }
                    onClick={() => {
                        const onlyViewChanged =
                            value === comparisonMode &&
                            selectedContexts.every(context =>
                                comparedContexts.includes(context)
                            )
                        if (onlyViewChanged) {
                            onComparisonViewChange(tempMode)
                            onClose()
                            return
                        }
                        const comparisonUrl = constructGraphComparisonUrl(
                            currentGraphName,
                            targetUsername,
                            valueToOperation(value),
                            selectedContexts,
                            isPublic
                        )
                        onClose()

                        sendToAnalytics({
                            userId: viewerUsername,
                            event: 'compare_graphs',
                            data: {
                                action: 'submit_comparison',
                                type: value,
                            },
                        })

                        if (typeof posthog != 'undefined') {
                            posthog.capture('compare_graphs', {
                                action: 'submit_comparison',
                            })
                        }

                        /**
                         * TODO: REPLACE WITH react router link once react-route is defined
                         */
                        window.location.href = comparisonUrl
                    }}
                >
                    Visualise
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

const valueToOperation = value => {
    switch (value) {
        case 0:
            return 'INTERSECTION'
        case 1:
            return 'DIFFERENCE'
        case 2:
            return 'COMBINATION'
        case 3:
            return 'EMBED'
    }
}

const identifyComparisonType = () => {
    const urlParameters = new Proxy(
        new URLSearchParams(window.location.search),
        {
            get: (searchParams, prop) => searchParams.get(prop),
        }
    )

    if (urlParameters.addcontext && urlParameters.interpret) return 3
    if (urlParameters.addcontext && urlParameters.merge) return 2

    if (
        urlParameters.addcontext &&
        urlParameters.highlight &&
        urlParameters.missing
    )
        return 1
    if (urlParameters.addcontext || !urlParameters.addcontext) return 0
}
